import {useEffect, useLayoutEffect, useRef, useState} from "react";
import {Currency, CurrencyDefinition} from "../../http/protocol";
import {useDispatch, useSelector} from "react-redux";
import {AppState} from "../../RootReducer";
import {setActiveCurrency, showBalanceDropdown} from "../../user/UserActions";
import "./CasinoCurrencySelector.scss";
import CurrencyFormatter from "../../utils/CurrencyFormatter";
import Big from "big.js";
import {showCashier} from "../../payments/DepositActions";
import DropdownArrow from "../../layout/DropdownArrow";
import Loader from "../../layout/Loader";
import {CashierSection} from "../../payments/PaymentsReducer";
import {getActiveCurrency, getVisibleCurrencies} from "../../selectors/user-selectors";
import {getCurrencyDefinitions, getPreferredFiatExchange, showFiatValue} from "../../selectors/settings-selectors";
import {hasFiatExchangeAmount, toFiatAmount, getCurrencyAmount} from "../../currency/currency-utils";
import {t} from "../../i18n";
import {BrandLink} from "../../ui/buttons/buttons";
import { setToLastBalanceUpdateFrequency, startBalanceUpdate } from "../../settings/SettingsActions";
import { Checkbox } from "../../ui/notifications/NotificationsComponent";

export const getSystemCurrencySymbol = (code : string) => {
    if(code.toUpperCase() == "EUR") {
        return "€";
    } else if(code.toUpperCase() == "USD") {
        return "$";
    }
    return "";

}

export const CasinoCurrencySelector = ( props : {}) => {
    const dispatch = useDispatch();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const initialBalanceFetched = useSelector((app : AppState) => app.user.initialBalanceFetched);
    const active = useSelector((app : AppState) => getActiveCurrency(app));
    const showBalance = useSelector((app : AppState)=> app.user.showBalance);
    const currencies = useSelector((app : AppState) => getVisibleCurrencies(app))
    const displayDropDown = currencies.length > 0;
    const balances = useSelector((app : AppState) => app.user.balances);
    const [show, setShow] = useState(false);
    const [height, setHeight] =  useState(0);
    const showFiat = useSelector((app : AppState) => showFiatValue(app))
    const currencyDefinitions = useSelector((app : AppState) => getCurrencyDefinitions(app));
    const preferredExchangeCurrency = useSelector((app : AppState) => getPreferredFiatExchange(app));

    const storedValue = localStorage.getItem('hideSmallAmounts') === 'true';
    const [hideSmallAmounts, setHideSmallAmounts] = useState<boolean>(storedValue);

    const handleToggle = () => {
        const newValue = !hideSmallAmounts;
        setHideSmallAmounts(newValue);
        localStorage.setItem('hideSmallAmounts', String(newValue));
    };

    const handleClickOutside = (e:any) =>  {
        if (!e.target || wrapperRef?.current?.contains(e.target as any)) {
           return;
        }
        setShow(false);
        dispatch(showBalanceDropdown(false))
    }

    const resize = () => {
        setShow(false);
    }

    useEffect(()=>{
        window.addEventListener('mousedown', handleClickOutside);
        window.addEventListener("resize", resize);
        return () => {
            window.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("resize", resize)
        }
    },[dispatch]);

    function calculateHeight() {
        const dropdown = document.querySelector("#currencyDropdown");
        const availableHeight = window.innerHeight - 220;
        let clientHeight = dropdown?.clientHeight ?? 0;
        if (clientHeight != 0 && clientHeight > availableHeight) {
            setHeight(availableHeight);
        } else {
            setHeight(0);
        }
    }

    useLayoutEffect(()=>{
        calculateHeight();
    },[show]);

    useEffect(() => {
        show ? dispatch(startBalanceUpdate() as any) : dispatch(setToLastBalanceUpdateFrequency() as any);
    }, [show, dispatch]);

    const  onClick = (code:string) =>  {
        // Create a custom event
        const changeCurrencyEvent = new CustomEvent('changeCurrencyEvent', {
                detail: { currency: code } // Additional data can be passed in detail
            });
        // Dispatch the event
        document.dispatchEvent(changeCurrencyEvent);
        dispatch(setActiveCurrency(code));
        setShow(false);
        dispatch(showBalanceDropdown(false));
    }

    const getBalance = (currency:string):number => {
        let activeBalance:number = 0;
        if(balances && balances.length > 0) {
            balances.map((balance) => {
                if(balance.balance && balance.balance.currencyCode == currency) {
                    activeBalance = balance.balance && balance.balance.amount ? balance.balance.amount : 0;
                }
            })
        }
        return activeBalance;
    }


    const format = (currency? : Currency, initialBalanceFetched? : boolean) => {

        if(!initialBalanceFetched || !currency || !currency.code || (balances && balances.length === 0)) {
            return <div className="balance-loader-container"><Loader /></div>
        }
        return CurrencyFormatter.formatWithSymbol(currency, Big(getBalance(currency.code)),true);
    }

    const showDeposit = () =>  {
        document.body.style.overflow = "";
        dispatch(showCashier(true,CashierSection.DEPOSIT));
    };


    const balanceToFiatAmount = (code : string, fiatCode : string) => {
        let balance = getBalance(code);
        return toFiatAmount(code,balance,fiatCode,currencyDefinitions);
    }

    let hasExchange = "";
    let hasFiat = active?.code && hasFiatExchangeAmount(active?.code, preferredExchangeCurrency, currencyDefinitions);
    if(hasFiat) {
        hasExchange = "has-exchange";
    }

    const filteredCurrencies = currencies.filter((currency: CurrencyDefinition) => {
        if (hideSmallAmounts) {
            const balance = balances.find(b => b.balance?.currencyCode === currency.code);

            if (!balance) {
                // if balance is not found for the currency, filter out the currency
                return false;
            }

            const isActiveCurrency = active?.code === currency.code;
            if (isActiveCurrency) {
                // always show active currency
                return true;
            }

            const currencyAmount = getCurrencyAmount(currency, balance, "EUR", currencyDefinitions);

            switch (currencyAmount.type) {
                case "crypto":
                    // crypto, compare euroValue
                    return currencyAmount.euroAmount?.cmp(new Big(1)) === 1;

                case "fiat":
                    // fiat, compare the fiat amount
                    return currencyAmount.amount > 1;

                default:
                    console.warn("Unknown currency type", currencyAmount);
                    return false;
            }

        }
        return true;
    });

    // Put active currency at the top
    if (active) {
        const activeIndex = filteredCurrencies.findIndex(c => c.code === active.code);

        if (activeIndex > 0) { // Ensure the currency exists and isn't already at the top
            const [activeCurrency] = filteredCurrencies.splice(activeIndex, 1); // Remove the active currency
            filteredCurrencies.unshift(activeCurrency); // Move it to the top
        }
    }

    // Prevent click propagation from dropdown content to avoid closing
    const handleDropdownClick = (e: React.MouseEvent) => {
        scrollToTop();
        e.stopPropagation();
    }

    const handleBalancesClick = () => {
        setShow(false);
        dispatch(showBalanceDropdown(false));
    }

    const showToggle = currencies.length > 1;

    const scrollToTop = () => {
        const listElement = listRef.current
        listElement?.scrollTo(0,0);
    }

    return (
        <div className="currency-selector-container" ref={wrapperRef}>
            <div className={`currency-selector ${showBalance} ${hasExchange}`} id="currency-selector-button"
                 onClick={() => {
                     scrollToTop();
                     setShow(!show)
                     dispatch(showBalanceDropdown(!show))
                 }}>
                { showBalance ? <span className="balance">
                    {(!showFiat || !hasFiat) && <span>{format(active,initialBalanceFetched)}</span>}
                    {showFiat && hasFiat && active?.code &&
                        <span className={"balance-with-crypto-symbol"}>
                            {balanceToFiatAmount(active.code, preferredExchangeCurrency)}
                            <i className={"top-menu-currency-icon currency-icon currency-icon-"+active.code?.toLocaleLowerCase()}></i>
                        </span>
                    }
                </span> : '' }
                { !showBalance && <span className="balance">{(active?.code ?? "")}</span> }
                {displayDropDown && <DropdownArrow className="currency-arrow" showDropdown={show ? true : false}/>}
                {displayDropDown && (
                    <div 
                        className={show ? `currency-selector-dropdown show` : "currency-selector-dropdown"} 
                        id={"currencyDropdown"}
                        onClick={handleDropdownClick}
                    >
                        <div className={`currency-dropdown-wrapper`}>

                            <div ref={listRef} className={"currency-list-currencies " + (height != 0 ? "scroll" : "")} style={{height : height != 0 ? height + "px" : "auto"}}>
                                {filteredCurrencies?.map( (c : CurrencyDefinition) => (
                                    <div className={`currency-list-container ${c.code == active?.code ? 'active' : ''}`} onClick={()=> c && c.code && onClick(c.code)} key={c.code}>
                                        <div className={`currency-selector-list`}>
                                            <div className={"currency-icon-container hide-in-context"}>
                                                <i className={"currency-icon currency-icon-"+c.code?.toLocaleLowerCase()}/>
                                            </div>
                                            <div className={"currency-name"}>
                                                {c.name}
                                            </div>
                                            <div className={"currency-balance "  + (c.code && hasFiatExchangeAmount(c.code, preferredExchangeCurrency, currencyDefinitions) ? "has-exchange" : "")}>
                                                <span className={"original-balance"}>{ c && format(c, initialBalanceFetched)}</span>
                                                {c.code && hasFiatExchangeAmount(c.code, preferredExchangeCurrency, currencyDefinitions) &&
                                                <span className={"exchange"}>{balanceToFiatAmount(c.code, preferredExchangeCurrency)}</span>
                                                }
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="currency-list-container button" >
                                <div className="currency-selector-list button-container">
                                    <BrandLink 
                                        className={"btn btn-primary small"} 
                                        to={"/account/balances"}
                                        onClick={handleBalancesClick}
                                    >
                                        {t("balances-page.balance-button")}
                                    </BrandLink>
                                    {showToggle &&
                                      <div className={"checkbox-wrapper"}>
                                          <span className={"checkbox-label"}>Hide small</span>
                                          <Checkbox checked={hideSmallAmounts} onChange={(e) => handleToggle()} />
                                      </div>}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
export default CasinoCurrencySelector;